import axios from 'axios';
import { BACKEND_URL } from '@/utils/apiConfig';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

/**
 * Diagnose push support without triggering any permission or subscription.
 * Returns { supported, permission, swReady, hasSubscription, vapidKey, error? }
 */
export async function getPushDiagnostics() {
  const result = {
    supported: 'serviceWorker' in navigator && 'PushManager' in window,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
    swReady: false,
    hasSubscription: false,
    vapidKey: !!VAPID_PUBLIC_KEY,
  };
  if (!result.supported) return result;
  try {
    const reg = await navigator.serviceWorker.ready;
    result.swReady = !!reg;
    const sub = await reg.pushManager.getSubscription();
    result.hasSubscription = !!sub;
  } catch (e) {
    result.error = e.message;
  }
  return result;
}

/**
 * Subscribe to push notifications.
 * Returns { subscription, error? } — always returns an object (never null).
 */
export async function subscribeToPush() {
  if (!VAPID_PUBLIC_KEY) {
    return { subscription: null, error: 'VAPID key not configured' };
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { subscription: null, error: 'Push API not supported in this browser' };
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    return { subscription: null, error: 'Service worker registration failed' };
  }

  await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { subscription: null, error: `Notification permission ${permission}` };
  }

  try {
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    await sendSubscriptionToServer(subscription);
    return { subscription };
  } catch (err) {
    console.error('[Push] Subscription failed:', err);
    return { subscription: null, error: err.message || 'Subscription failed' };
  }
}

async function sendSubscriptionToServer(subscription) {
  const subJSON = subscription.toJSON();
  await axios.post(`${BACKEND_URL}/api/notifications/subscribe`, {
    subscription: {
      endpoint: subJSON.endpoint,
      keys: subJSON.keys,
    },
  }, { withCredentials: true });
}

export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      await axios.delete(`${BACKEND_URL}/api/notifications/unsubscribe`, { withCredentials: true });
    }
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
  }
}
